import { FormProvider, useForm } from 'react-hook-form';
import Modal from '../../../components/common/Modal';
import TextInput from '../../../components/common/TextInput';
import Select from '../../../components/common/Select';
import Button from '../../../components/common/button';
import { toast } from 'react-toastify';
import { useEffect, useState } from 'react';
import useGetComponentsList from '../../../hooks/use-get-components-list';
import useGetDepartmentsData from '../../../hooks/use-get-department-data';
import { IDepartment } from '../../../utils/types';
import Spinner from '../../../components/common/Spinner';
import useAddCategoryComponent from '../../../hooks/use-add-category-component';
import useUpdateCategoryComponent from '../../../hooks/use-update-category-component';
import Switch from '../../../components/common/Switch';
import useAddSubmenuComponent from '../../../hooks/use-add-submenu-component';


interface IOptions {
  label: string;
  value: string;
}

interface IFormData {
  [key: string]: any; // Dynamic fields based on requiredKey
}

interface AddItemModalProps {
  isOpen: boolean;
  closeModal: () => void;
  onSuccess: (data: any, isEdit: boolean) => void;
  itemData?: any;
  isEditItem?: boolean;
  currentListLength?: number;
  componentName: string;
  allComponentsData: any;
  currentName?: string
}


const AddItemModal = ({
  isOpen = false,
  closeModal,
  onSuccess,
  itemData,
  isEditItem = false,
  currentListLength = 0,
  componentName,
  allComponentsData,
  currentName
}: AddItemModalProps) => {
  const { mutate: addCategoryComponent, isPending: isAddCategoryComponentPending } = useAddCategoryComponent();
  const { mutate: addSubmenuComponent, isPending: isAddSubmenuComponentPending } = useAddSubmenuComponent();
  const { mutate: updateCategoryComponent, isPending: isUpdateCategoryComponentPending } = useUpdateCategoryComponent({ id: itemData?._id || '' });

  const [requiredFields, setRequiredFields] = useState<string[]>([]);
  const [departments, setDepartments] = useState<IOptions[]>([]);
  const [viewTypeOptions, setViewTypeOptions] = useState<IOptions[]>([]);
  const { data: departmentsData, } = useGetDepartmentsData();

  useEffect(() => {
    if (departmentsData?.result?.data?.departmentsData) {
      setDepartments(departmentsData.result.data.departmentsData.map((dept: IDepartment) => ({ label: dept.departmentName, value: dept._id })));
    }
  }, [departmentsData])
  const methods = useForm<IFormData>({
    defaultValues: {}
  });

  const { handleSubmit, reset, watch, setValue } = methods;
  const selectedViewType = watch('viewType');
  const { data: componentsListData, isPending: isComponentsListPending } = useGetComponentsList(
    selectedViewType?.value || itemData?.viewType || '',
    isOpen
  );
  const ignoredViewTypes = [
    'button',
    'dropdown',
    'fixedbutton',
    'incrementwithimagewithoutrequest',
    'laundry',
    'submenuwithcart',
    'wakeupcall'
  ];

  const ignoreRequiredFields = ['type'];

  useEffect(() => {
    setRequiredFields([])
    if (viewTypeOptions.length === 0) {
      setViewTypeOptions(componentsListData?.result?.data?.items?.filter((item: { viewType: string, _id: string }) => !ignoredViewTypes.includes(item.viewType)).map((item: { viewType: string, _id: string }) => ({ label: item.viewType, value: item.viewType })) || []);
    }
    if (componentsListData?.result?.data?.items?.[0]?.requiredKey) {
      setRequiredFields(componentsListData.result.data.items[0].requiredKey);
    }
  }, [componentsListData]);


  useEffect(() => {
    if (!isOpen) {
      setViewTypeOptions([])
    }
  }, [isOpen]);
  useEffect(() => {
    if (itemData && isEditItem && viewTypeOptions.length > 0 && departments.length > 0) {
      const selectedType = viewTypeOptions.find(option => option.value === itemData.viewType);
      const selectedDepartment = departments.find(dept => dept.value === itemData.department);

      setValue('viewType', selectedType || null);
      reset({
        name: itemData.name || '',
        viewType: selectedType || null,
        description: itemData.description || '',
        title: itemData.title || '',
        subtitle: itemData.subtitle || '',
        link: itemData.link || '',
        amount: itemData.amount || '',
        quantity: itemData.quantity || '',
        maxQuantityPerRoom: itemData.maxQuantityPerRoom || 10,
        department: selectedDepartment || null,
        goTo: itemData.goTo || '',
        inactive: itemData.inactive || false
      });
    } else if (!isEditItem) {
      reset({
        name: '',
        viewType: null,
        description: '',
        title: '',
        subtitle: '',
        link: '',
        maxQuantityPerRoom: 1,
        goTo: '',
        inactive: false,
        categoryName: '',
        submenuViewRank: 1,
        isVisible: true
      });
    }
  }, [isOpen, itemData, isEditItem, reset, viewTypeOptions, departments]);

  const handleFormSubmit = (data: IFormData) => {
    if (!data.viewType) {
      toast.error('Please select a view type');
      return;
    }

    // Only include filtered required fields
    const filteredData = requiredFields
      .filter(field => !ignoreRequiredFields.includes(field))
      .reduce((acc, field) => {
        if (data[field] !== undefined) {
          // Extract value from department object
          if (field === 'department' && data[field]?.value) {
            acc[field] = data[field].value;
          } else if (['amount', 'quantity', 'maxQuantityPerRoom'].includes(field)) {
            acc[field] = parseInt(data[field]) || 0;
          } else {
            acc[field] = data[field];
          }
        }
        return acc;
      }, {} as IFormData);

    const newItem = {
      _id: itemData?._id || Date.now().toString(),
      viewType: selectedViewType.value,
      viewRank: itemData?.viewRank || (currentListLength + 1),
      parentComponentId: allComponentsData?.cateogry.find((cat: any) => cat.displayName === componentName)?._id || '',
      inactive: data.inactive ?? false,
      isVisible: isEditItem ? itemData.isVisible : true,
      ...filteredData
    };

    if (isEditItem) {
      updateCategoryComponent(newItem, {
        onSuccess: () => {
          onSuccess(newItem, true);
          toast.success('Item updated successfully');
          closeModal();
        },
        onError: () => {
          toast.error('Failed to update item');
        }
      });
    } else {
      const formData = {
        ...newItem,
        parentComponentId: allComponentsData?.cateogry.find((cat: any) => cat.displayName === componentName)?._id || '',
      };

      if (selectedViewType?.value === 'submenu') {
        if (currentName === undefined) {
          toast.error('Current category name is undefined');
          return;
        }
        if (!data.goTo) {
          toast.error('Please provide a goTo value for the submenu');
          return;
        }
        if (currentName.trim() === data.goTo.trim()) {
          toast.error('The submenu name and goTo value cannot be the same');
          return;
        }
        const submenuData = {
          categoryName: currentName,
          componentName: "submenu",
          data: {
            name: `${data.name}`,
            goTo: data.goTo,
            viewRank: 1
          },
          isVisible: true,
          isSubmenu: true,
          isDining: componentName.toLowerCase() === 'dining'
        }

        addSubmenuComponent(submenuData, {
          onSuccess: (res: any) => {
            onSuccess({ ...newItem, name: submenuData.data.name }, false);
            toast.success(res?.message || 'Item added successfully');
            closeModal();
          },
          onError: (err: any) => {
            toast.error(err?.response?.data?.message || 'Failed to add item');
          }
        });
        return;
      }
      addCategoryComponent(formData, {
        onSuccess: (res: any) => {
          onSuccess(newItem, false);
          toast.success(res?.message || 'Item added successfully');
          closeModal();
        },
        onError: (err: any) => {
          toast.error(err?.response?.data?.message || 'Failed to add item');
        }
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      closeModal={closeModal}
      title={isEditItem ? 'Edit Item' : 'Add New Item'}
      className='w-144.75'
    >
      <div className='h-96 overflow-y-auto pr-4 pl-1'>
        <FormProvider {...methods} key={1}>
          <form className="space-y-4">
            {/* View Type */}
            <Select
              name="viewType"
              label="View Type"
              value={selectedViewType}
              options={viewTypeOptions}
              placeholder="Select view type"
              isDisabled={isEditItem || isUpdateCategoryComponentPending || isAddCategoryComponentPending || isAddSubmenuComponentPending}
              required
            />

            {/* Dynamic Required Fields */}
            {isComponentsListPending ? <div className='w-full flex justify-center align-middle'><Spinner /></div> : selectedViewType && requiredFields.filter(field => !ignoreRequiredFields.includes(field)).map((field, index) => {
              if (field === 'department') {
                return (
                  <Select
                    key={index}
                    name={field}
                    label={field.charAt(0).toUpperCase() + field.slice(1)}
                    options={departments}
                    rules={{
                      required: 'Department is required',
                    }}
                    placeholder="Select department"
                    isDisabled={isUpdateCategoryComponentPending || isAddCategoryComponentPending || isAddSubmenuComponentPending}
                    required
                  />
                )
              }

              if (field === 'link') {
                return (
                  <TextInput
                    key={index}
                    name={field}
                    label={field.charAt(0).toUpperCase() + field.slice(1)}
                    placeholder="https://example.com"
                    rules={{
                      required: `${field} is required`
                    }}
                    type="url"
                    disabled={isUpdateCategoryComponentPending || isAddCategoryComponentPending || isAddSubmenuComponentPending}
                    required
                  />
                )
              }
              if (field === 'amount' || field === 'quantity' || field === 'maxQuantityPerRoom') {
                return (
                  <TextInput
                    key={index}
                    name={field}
                    label={field.charAt(0).toUpperCase() + field.slice(1)}
                    placeholder={`Enter ${field}`}
                    type={field === 'amount' || field === 'quantity' || field === 'maxQuantityPerRoom' ? 'number' : 'text'}
                    rules={{
                      required: `${field.charAt(0).toUpperCase() + field.slice(1)} is required`
                    }}
                    disabled={isUpdateCategoryComponentPending || isAddCategoryComponentPending || isAddSubmenuComponentPending}
                    required
                  />
                );
              }
              if (field === 'inactive') {
                return (
                  <Switch
                    label="Inactive"
                    name="inactive"
                    checked={watch('inactive')}
                  />
                )
              }
              return (
                <>
                  <TextInput
                    key={index}
                    name={field}
                    label={field.charAt(0).toUpperCase() + field.slice(1)}
                    placeholder={`Enter ${field}`}
                    rules={{
                      required: `${field} is required`
                    }}
                    disabled={isUpdateCategoryComponentPending || isAddCategoryComponentPending || isAddSubmenuComponentPending}
                    required
                  />

                </>
              )
            })}


            {/* Form Actions */}
            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="default"
                onClick={closeModal}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                className="flex-1"
                onClick={handleSubmit(handleFormSubmit)}
                disabled={isAddCategoryComponentPending || isUpdateCategoryComponentPending || isAddSubmenuComponentPending}
              >
                {isEditItem ? 'Update Item' : 'Add Item'}
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
    </Modal>
  );
};

export default AddItemModal;